import React, { memo, useState, useEffect } from 'react';
import moment from 'moment';
import { get, isEqual, isString } from 'lodash';
import PropTypes from 'prop-types';
import SSPList from '../../../common/components/SSPList/index';
import { iotOnDateFormat } from '../../../common/services/ValidationRules';
import {
  onDatePickerFilter,
  onTextFilterNosaving as onTextFilter,
} from '../../../common/components/CommonTableDataFilter/CommonTableDataFilter';
import { connectorIdOptions } from '../../../common/services/ChargerFormBuilidingService';
import { TableSelect } from '../../ChargersList/components/ChargersTableComponents';
import { failuresErrorTypesWithEmpty, failuresSeverities } from '../../AdminTools/components/Dictionary/components/DictionaryStatics';
import { errorSeverityColumn, errorTypeColumn, moduleNameColumn } from '../../../common/components/CommonTableColumns';
import APIClient from '../../../common/services/APIClient';

export const faultsSSPId = 'failures';

const defaultSorted = { id: 'e_ts', desc: true };

const getOptionVal = (opt) => (isString(opt.label) ? opt.label.toUpperCase() : opt.value);

const FaultsTab = memo(
  ({ charger, isMobile, isModal, config, onGetDataAction, exportLimit, onExport }) => {
    const [modules, setModules] = useState([]);

    const connectorIdsOptions = connectorIdOptions(charger, config, true);

    let faultsColumns = [
      {
        Header: 'Connector Name',
        id: 'connector_id',
        accessor: 'connector_name',
        minWidth: 130,
        Filter: ({ filter, onChange }) => (
          <TableSelect options={connectorIdsOptions} onChange={onChange} value={filter ? filter.value : ''} />
        ),
      },
      {
        Header: 'Error UID',
        id: 'error_uid',
        accessor: 'error_uid',
        minWidth: 70,
        Filter: onTextFilter,
      },
      {
        Header: 'Date/Time (UTC)',
        id: 'e_ts',
        accessor: 'e_ts',
        Cell: (item) => moment.unix(item.original.e_ts).utc().format(iotOnDateFormat),
        minWidth: 150,
        Filter: onDatePickerFilter,
      },
      errorTypeColumn(failuresErrorTypesWithEmpty),
      errorSeverityColumn(failuresSeverities),
      {
        Header: 'Error Code',
        id: 'e_code',
        accessor: 'e_code',
        Filter: onTextFilter,
      },
      moduleNameColumn(modules),
      {
        Header: 'Short Description',
        id: 'e_short_description',
        className: 'break-string-pre-wrap',
        minWidth: 150,
        accessor: 'e_short_description',
        Filter: onTextFilter,
      },
      {
        Header: 'Full Description',
        id: 'e_full_description',
        className: 'break-string-pre-wrap',
        minWidth: 150,
        accessor: 'e_full_description',
        Filter: onTextFilter,
      },
      {
        Header: 'Scenarios',
        id: 'scenarios',
        accessor: 'scenarios',
        className: 'break-string-pre-wrap',
        minWidth: 150,
        Filter: onTextFilter,
      },
      {
        Header: 'Resolution Steps',
        id: 'resolution',
        className: 'break-string-pre-wrap',
        accessor: 'resolution',
        minWidth: 150,
        Filter: onTextFilter,
      },
    ];

    const fields = get(config, 'chargerDetails.faults.fields', []);

    if (fields.length > 0) {
      faultsColumns = faultsColumns.filter((column) => fields.includes(column.id));
    }

    const getOptions = (customerId) => {
      APIClient.get('/reports/faults/modules', customerId ? { params: { customerId } } : null)
        .then((resp) => {
          const newModules = get(resp, 'data', [])
            .map((module) => ({ value: module, label: module }))
            .sort((a, b) => {
              const alc = getOptionVal(a);
              const blc = getOptionVal(b);

              if (alc > blc) {
                return 1;
              }

              if (alc < blc) {
                return -1;
              }

              return 0;
            });

          setModules(newModules);
        })
        .catch((err) => {
          console.error(err);
        });
    };

    useEffect(() => {
      getOptions();
    }, []);

    return (
      <div data-activetab="faults" className={`create-edit-static-list${isMobile ? ' mobile' : ''}${isModal ? ' modal' : ''}`}>
        <SSPList
          exportLimit={exportLimit}
          isModal={isModal}
          mobileSticky
          onGetDataAction={onGetDataAction}
          defaultSorted={defaultSorted}
          defaultPageSize={20}
          id={faultsSSPId}
          columns={faultsColumns}
          clearFilterOnInit
          multiselect
          exportable
          onExport={onExport}
          rowsFreeHeight
        />
      </div>
    );
  },
  (prevProps, nextProps) =>
    isEqual(prevProps.isMobile, nextProps.isMobile) &&
    isEqual(prevProps.isModal, nextProps.isModal) &&
    isEqual(prevProps.charger, nextProps.charger) &&
    isEqual(prevProps.config, nextProps.config),
);

FaultsTab.propTypes = {
  charger: PropTypes.object,
  config: PropTypes.object,
  isMobile: PropTypes.bool,
  isModal: PropTypes.bool,
  onGetDataAction: PropTypes.func,
};

export default FaultsTab;
